import type { BoundaryPartResponse, LatLon, Position, SessionInput } from './types';

const APPLY_BOUNDARY_TYPE = 'APPLY';
const NO_ENTRY_BOUNDARY_TYPE = 'NO_ENTRY';

export const latLonToPosition = ({ latLon }: { latLon: LatLon }): Position => [
    latLon.lon,
    latLon.lat
];

export const positionToLatLon = ({ position }: { position: Position }): LatLon => ({
    lat: position[1],
    lon: position[0]
});

export const latLonsToPositions = ({ latLons }: { latLons: LatLon[] }): Position[] =>
    latLons.map(latLon => latLonToPosition({ latLon }));

export const positionsToLatLons = ({ positions }: { positions: Position[] }): LatLon[] =>
    positions.map(position => positionToLatLon({ position }));

// The AcreConnect API returns boundary Polygons in GeoJSON shape
// (`coordinates: Position[][]`, where `coordinates[0]` is the exterior ring
// as an array of `[lon, lat]` tuples). Orval generated the wrong static type
// from the backend schema — it declares `coordinates?: Coordinate[]` using
// the JTS `{ x, y }` object shape, which never appears at runtime. We coerce
// through `unknown` at the access point to keep the cast tight and the rest
// of the module type-safe.
const extractLatLonsFromBoundaryPart = (part: BoundaryPartResponse): LatLon[] => {
    const rawCoordinates = part.boundary.coordinates as unknown as Position[][] | undefined;
    const exteriorRing = rawCoordinates?.[0];

    if (!exteriorRing || exteriorRing.length === 0) {
        return [];
    }

    return exteriorRing.map(position => positionToLatLon({ position }));
};

export const boundaryPartsToSessionInput = ({
    boundaryParts
}: {
    boundaryParts: BoundaryPartResponse[];
}): SessionInput => {
    const applyPart = boundaryParts.find(part => part.boundaryType === APPLY_BOUNDARY_TYPE);

    if (!applyPart) {
        throw new Error('No APPLY boundary part found in boundary');
    }

    const boundary = extractLatLonsFromBoundaryPart(applyPart);
    const obstacles = boundaryParts
        .filter(part => part.boundaryType === NO_ENTRY_BOUNDARY_TYPE)
        .map(part => extractLatLonsFromBoundaryPart(part));

    return { boundary, obstacles };
};

export type { LatLon, Position, SessionInput };
